import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Film, ExternalLink, Globe, Loader2, ShieldCheck } from "lucide-react";
import { API } from "../lib/api";
import { getI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { useSecurityStrikes, SecurityWarningModal } from "./SecurityWarningModal";

const PALETTE = {
  primary: "#7B5CE0",
  primaryDark: "#5A3FC0",
  border: "#E8E1FA",
  text: "#2A1F4A",
  textSoft: "#6E6688",
  surface: "#0E0A1F",
};

/**
 * Premium VideoPlayer for VIP lessons.
 *
 * Responsibilities:
 *  - Glassmorphism wrapper with skeleton + spinner while metadata loads.
 *  - Range-streamed MP4 via backend (preload=metadata for fast first frame).
 *  - YouTube/Vimeo embed with optimised params (no related, no branding,
 *    rel=0, modestbranding).
 *  - Anti-download surface (no contextmenu, controlsList=nodownload,
 *    disablePictureInPicture, blocked drag/save shortcuts).
 *  - Live moving watermark (user email + ID + clock) over the video.
 *  - DevTools detector (window-size delta + debugger-timing).
 *  - Strike reporter wired to `/api/security/strike`.
 */
export default function VideoPlayerPremium({
  video,
  language = "it",
  lessonId,
}) {
  const t = getI18n(language).video;
  const { user } = useAuth();
  const sec = useSecurityStrikes({ lang: language, lessonId });

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [windowBlurred, setWindowBlurred] = useState(false);

  // ─── Cross-language hint ───
  const langMismatch = video?.language && video.language !== language;
  const labelMap = language === "es"
    ? { it: "italiano", es: "español" }
    : { it: "italiano", es: "spagnolo" };
  const hintText = useMemo(() => {
    if (!langMismatch) return null;
    const videoLang = labelMap[video.language] || video.language;
    const userLang = labelMap[language] || language;
    return `${t.lang_mismatch_prefix} ${videoLang}. ${t.lang_mismatch_suffix} ${userLang}.`;
  }, [langMismatch, video?.language, language, t, labelMap]);

  // ─── Window blur / visibility ───
  // When the user tabs away during playback, dim the video — common screen-
  // recording vector starts with the user defocusing the tab.
  useEffect(() => {
    const onBlur = () => {
      setWindowBlurred(true);
      const v = videoRef.current;
      if (v && !v.paused) {
        sec.report("window_blur_during_video");
      }
    };
    const onFocus = () => setWindowBlurred(false);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) onBlur();
      else onFocus();
    });
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, [sec]);

  // ─── Global shortcut + context-menu blockers ───
  // Only active while this component is mounted (i.e., a video is on screen).
  useEffect(() => {
    const onKeyDown = (e) => {
      const k = (e.key || "").toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      // Ctrl/Cmd + S (save)
      if (mod && k === "s") {
        e.preventDefault();
        sec.report("save_shortcut");
        return;
      }
      // Ctrl/Cmd + U (view source)
      if (mod && k === "u") {
        e.preventDefault();
        sec.report("view_source");
        return;
      }
      // Ctrl/Cmd + P (print)
      if (mod && k === "p") {
        e.preventDefault();
        sec.report("print_attempt");
        return;
      }
      // F12
      if (k === "f12") {
        e.preventDefault();
        sec.report("devtools_shortcut");
        return;
      }
      // Ctrl+Shift+I / Ctrl+Shift+J / Ctrl+Shift+C
      if (mod && e.shiftKey && ["i", "j", "c"].includes(k)) {
        e.preventDefault();
        sec.report("devtools_shortcut");
        return;
      }
      // PrintScreen (best-effort — many OS intercept before browser)
      if (k === "printscreen") {
        sec.report("screenshot_attempt");
      }
    };
    const onContext = (e) => {
      // Only block when the right-click is on or inside the player.
      if (containerRef.current?.contains(e.target)) {
        e.preventDefault();
        sec.report("right_click");
      }
    };
    const onCopy = (e) => {
      if (containerRef.current?.contains(e.target)) {
        e.preventDefault();
        sec.report("copy_attempt");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("contextmenu", onContext);
    window.addEventListener("copy", onCopy);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("contextmenu", onContext);
      window.removeEventListener("copy", onCopy);
    };
  }, [sec]);

  // ─── DevTools detector (best-effort) ───
  // Two signals: (1) window outer/inner size delta crosses a threshold,
  // (2) `debugger` instruction takes longer than a few ms (set when devtools
  // is open). We fire at most once per minute.
  useEffect(() => {
    let lastFire = 0;
    const fire = (reason) => {
      const now = Date.now();
      if (now - lastFire > 60_000) {
        lastFire = now;
        sec.report("devtools_detected", { reason });
      }
    };
    const id = setInterval(() => {
      try {
        const wDelta = window.outerWidth - window.innerWidth;
        const hDelta = window.outerHeight - window.innerHeight;
        if (wDelta > 200 || hDelta > 250) fire("size_delta");
        // debugger timing trick: when devtools is open this runs slow
        const t0 = performance.now();
        // eslint-disable-next-line no-debugger
        // (intentionally no `debugger;` here — too disruptive for legit devs)
        const t1 = performance.now();
        if (t1 - t0 > 100) fire("perf_timing");
      } catch (_e) {
        // ignore
      }
    }, 1500);
    return () => clearInterval(id);
  }, [sec]);

  // ─── Watermark animation (moves every 6s) ───
  const [wmPos, setWmPos] = useState({ top: "15%", left: "8%" });
  useEffect(() => {
    const id = setInterval(() => {
      // Random tile so a single still frame won't be the only one with the
      // watermark in a corner — discourages screen-record/cropping.
      const top = 10 + Math.random() * 70;   // 10–80 vh of container
      const left = 5 + Math.random() * 75;   // 5–80 vw of container
      setWmPos({ top: `${top}%`, left: `${left}%` });
    }, 6000);
    return () => clearInterval(id);
  }, []);

  // ─── Renderers ───
  // Compute derived values BEFORE any early return so the hook order is
  // stable across renders (React Rules of Hooks).
  const kind = video?.kind;
  const stream_url = video?.stream_url;
  const embed_url = video?.embed_url;
  const external_url = video?.external_url;

  // Pre-compute the protected MP4 URL (auth still required server-side).
  const mp4Src = (kind === "upload" && stream_url)
    ? `${API}${stream_url.replace(/^\/api/, "")}`
    : null;

  // Tighten YouTube/Vimeo embeds for faster + cleaner playback.
  const tunedEmbed = useMemo(() => {
    if (!embed_url) return null;
    try {
      const u = new URL(embed_url);
      if (u.hostname.includes("youtube")) {
        u.searchParams.set("rel", "0");
        u.searchParams.set("modestbranding", "1");
        u.searchParams.set("playsinline", "1");
        u.searchParams.set("iv_load_policy", "3");
      }
      if (u.hostname.includes("vimeo")) {
        u.searchParams.set("dnt", "1");
        u.searchParams.set("title", "0");
        u.searchParams.set("byline", "0");
        u.searchParams.set("portrait", "0");
      }
      return u.toString();
    } catch (_e) {
      return embed_url;
    }
  }, [embed_url]);

  if (!video) return null;

  return (
    <>
      <SecurityWarningModal sec={sec} />
      <div
        ref={containerRef}
        data-testid="vip-video-player"
        className="rounded-2xl overflow-hidden relative"
        style={{
          background: "linear-gradient(135deg, rgba(123,92,224,0.06), rgba(255,255,255,0.4))",
          border: `1px solid ${PALETTE.border}`,
          boxShadow: "0 20px 60px -25px rgba(91,63,192,0.45)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
        onDragStart={(e) => { e.preventDefault(); sec.report("drag_attempt"); }}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 py-3"
             style={{
               background: `linear-gradient(135deg, ${PALETTE.primary}, ${PALETTE.primaryDark})`,
               color: "white",
             }}>
          <div className="flex items-center gap-2">
            <Film className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.18em] opacity-90">
              {t.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] opacity-90"
               title={t.protected}>
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="uppercase tracking-[0.16em] hidden sm:inline">
              {t.protected}
            </span>
          </div>
        </div>

        {/* Language hint */}
        {hintText ? (
          <div
            data-testid="vip-video-language-hint"
            role="status"
            className="flex items-start gap-2.5 px-4 py-2.5 text-xs border-b"
            style={{
              background: `${PALETTE.primary}0F`,
              borderColor: `${PALETTE.primary}33`,
              color: PALETTE.text,
            }}
          >
            <Globe className="h-3.5 w-3.5 shrink-0 mt-0.5"
                    style={{ color: PALETTE.primary }} />
            <p className="leading-snug">{hintText}</p>
          </div>
        ) : null}

        {/* Media surface */}
        <div className="relative" style={{ aspectRatio: "16 / 9", background: "#000" }}>
          {/* Skeleton / loader overlay */}
          {!loaded && (mp4Src || tunedEmbed) ? (
            <div
              data-testid="vip-video-skeleton"
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10"
              style={{
                background: "linear-gradient(135deg, #1a0f3e, #2a1750)",
                color: "white",
              }}
            >
              <Loader2 className="h-7 w-7 animate-spin opacity-80" />
              <p className="text-sm font-medium opacity-90">{t.loading}</p>
              <p className="text-[11px] opacity-60">{t.loading_hint}</p>
              {/* Shimmer bar */}
              <div className="mt-3 h-1 w-40 rounded-full overflow-hidden"
                   style={{ background: "rgba(255,255,255,0.12)" }}>
                <div className="h-full w-1/3 animate-pulse"
                     style={{ background: "linear-gradient(90deg, transparent, white, transparent)" }} />
              </div>
            </div>
          ) : null}

          {/* MP4 native */}
          {mp4Src ? (
            <video
              ref={videoRef}
              data-testid="vip-video-element"
              src={mp4Src}
              controls
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              playsInline
              preload="metadata"
              onLoadedMetadata={() => setLoaded(true)}
              onCanPlay={() => setLoaded(true)}
              onContextMenu={(e) => { e.preventDefault(); sec.report("context_menu"); }}
              className="block w-full h-full"
              style={{ background: "#000" }}
            />
          ) : tunedEmbed ? (
            <iframe
              data-testid="vip-video-iframe"
              src={tunedEmbed}
              title={t.label}
              className="block w-full h-full absolute inset-0"
              style={{ border: 0 }}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          ) : external_url ? (
            <a href={external_url} target="_blank" rel="noopener noreferrer"
               data-testid="vip-video-external-link"
               className="absolute inset-0 grid place-items-center"
               style={{ color: "white",
                        background: `linear-gradient(135deg, ${PALETTE.primary}, ${PALETTE.primaryDark})` }}>
              <div className="text-center">
                <Film className="h-7 w-7 mx-auto mb-2" />
                <p className="text-sm font-medium">{t.open_external}</p>
                <ExternalLink className="h-4 w-4 mx-auto mt-2 opacity-80" />
              </div>
            </a>
          ) : null}

          {/* Dynamic watermark — moves every 6s, only on the player surface */}
          {user ? (
            <div
              data-testid="vip-video-watermark"
              aria-hidden="true"
              className="absolute pointer-events-none select-none transition-all duration-700"
              style={{
                top: wmPos.top,
                left: wmPos.left,
                color: "rgba(255,255,255,0.28)",
                fontSize: "11px",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                lineHeight: 1.25,
                transform: "rotate(-8deg)",
                mixBlendMode: "screen",
              }}
            >
              <div>{user.email}</div>
              <div>id: {String(user.user_id || "").slice(0, 12)}</div>
              <div>{new Date().toLocaleString(language === "es" ? "es-ES" : "it-IT")}</div>
            </div>
          ) : null}

          {/* Window-blur dim overlay — fades in when the tab loses focus
              during playback to discourage external screen recording. */}
          {windowBlurred ? (
            <div
              data-testid="vip-video-blur-overlay"
              className="absolute inset-0 z-20 grid place-items-center"
              style={{
                background: "rgba(0,0,0,0.85)",
                backdropFilter: "blur(8px)",
                color: "white",
                fontSize: "13px",
              }}
            >
              {t.protected}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
